import type { BaseFilter } from "../../../MemberFilters/types";
import { useFilterProvider } from "@/react/components/FilterKit/FilterProvider";
import { useAudienceInfiniteQuery } from "@/react/hooks/useAudienceInfiniteQuery";
import { isEmpty } from "lodash";
import { useMemberDirectorySettings } from "@/react/components/MemberDirectory/useMemberDirectorySettings";
import { STATUS_OPTIONS_MAP } from "../../constants";
import { useMemo } from "react";
import { getOrderParamFromSort } from "@/react/helpers/urlHelpers/getOrderParamFromSort";

export interface UseSelectMembersInfiniteQueryV3Props {
  baseFilters?: BaseFilter[];
  defaultSort?: string;
  isAdminScope?: boolean;
}

export const useSelectMembersInfiniteQueryV3 = ({
  baseFilters = [],
  defaultSort,
  isAdminScope = false,
}: UseSelectMembersInfiniteQueryV3Props) => {
  const { filterParams } = useFilterProvider();

  const mappedFilters = useMemo(
    () =>
      filterParams.map(({ search_key, ...filter }) => ({
        ...filter,
        key: search_key,
      })),
    [filterParams],
  );

  const finalFilters = [...mappedFilters, ...baseFilters];

  const memberDirectorySettings = useMemberDirectorySettings();

  const order = defaultSort
    ? getOrderParamFromSort(defaultSort)
    : ({ created_at: "desc" } as const);

  const audienceInfiniteQuery = useAudienceInfiniteQuery({
    order,
    options: {
      keepPreviousData: false,
    },
    filters: finalFilters,
    scope: {
      ...memberDirectorySettings.scope,
      status: STATUS_OPTIONS_MAP.ACTIVE,
    },
    isAdminScope,
  });

  return {
    ...audienceInfiniteQuery,
    dataLength: audienceInfiniteQuery.totalQueryAudience,
    totalMembers: audienceInfiniteQuery.totalAudience,
    members: audienceInfiniteQuery.audience,
    isActiveQuery: !isEmpty(filterParams) || baseFilters.length > 0,
  };
};
